// @flow
import {concat, flatten, set} from 'lodash';
import {filterMarkdocTags} from 'docs/util';
import type {RenderNode} from '@markdoc/markdoc';
import type {Node, Edge, Element, NodeChildren, NodeAttributes} from '../types';

type SequenceDiagramDirection = 'LTR' | 'RTL';
type NodeHandle = 'right' | 'left' | 'top' | 'bottom';

// A sequenceActor node is the vertical line that represents an "actor" in a sequence diagram
// e.g. customer, server, client, stripe, etc
const buildSequenceActorNode = (
  attributes: NodeAttributes,
  children: NodeChildren,
  index: number,
): Node => {
  const {id} = attributes;
  return {
    id,
    type: 'sequenceActor',
    data: {heading: children, index},
    position: {x: 0, y: 0},
  };
};

const getIconLabel = (child: null | RenderNode): string => {
  if (!child || typeof child === 'string') {
    return '';
  }
  if (child.attributes.type === 'webhook') {
    return child.attributes.alt;
  }
  return '';
};

// A sequenceAction node represents an action that is happening between two actors
// e.g. making an API request, entering card details, etc
// Each sequenceAction is connected to two sequenceActor nodes by two edges
const buildSequenceActionNodeAndEdges = (
  attributes: NodeAttributes,
  children: NodeChildren,
  direction: SequenceDiagramDirection,
  row: number,
  start: number,
  end: number,
): [Edge, Node, Edge] => {
  const {from, to} = attributes;
  const icon = filterMarkdocTags(children, (tag) => tag.name === 'Icon')[0];
  const event = getIconLabel(icon);
  const text = filterMarkdocTags(children, (tag) => tag.name === 'Paragraph');

  // Handles tell react-flow-renderer where on the node to connect an edge
  // Handles are defined in the Node UI using the <Handle id="<id>" /> component
  const [handle1, handle2] =
    direction === 'LTR' ? ['left', 'right'] : ['right', 'left'];

  const getActionNodeHandles = (): [NodeHandle, NodeHandle] => {
    if (start === end) {
      return ['top', 'bottom'];
    } else {
      return [handle1, handle2];
    }
  };

  const getEdgeHandles = (): [string, string] => {
    const pos = start === end ? row - 1 : row;
    return [`${from}-${pos}-${handle2}`, `${to}-${row}-${handle1}`];
  };

  // react-flow-renderer need unique ids for each node and edge to draw the connections
  const node = `node-${row}-${from}-${to}`;
  const edge1 = `edge-${row}-${from}-${to}-1`;
  const edge2 = `edge-${row}-${from}-${to}-2`;

  const [edge1SourceHandle, edge2SourceHandle] = getEdgeHandles();

  const [
    actionNodeTargetHandle,
    actionNodeSourceHandle,
  ] = getActionNodeHandles();

  return [
    {
      id: edge1,
      source: from,
      target: node,
      arrowHeadType: 'arrowclosed',
      sourceHandle: edge1SourceHandle,
      targetHandle: actionNodeTargetHandle,
    },
    {
      id: node,
      type: 'sequenceAction',
      data: {
        text,
        row,
        event,
        from,
        to,
      },
      position: {x: 0, y: 0},
    },
    {
      id: edge2,
      source: node,
      target: to,
      arrowHeadType: 'arrowclosed',
      sourceHandle: actionNodeSourceHandle,
      targetHandle: edge2SourceHandle,
    },
  ];
};

export const buildSequenceDiagram = (
  attributes: NodeAttributes,
  children: NodeChildren | null,
): Array<Element> => {
  if (!children) {
    return [];
  }

  let row = 1;
  let index = 0;

  // Keep a list of the sequenceActor indicies in the diagram
  // so we can detect when the direction of the sequence changes
  const indicies = {};

  // Previous start/end points of the prior sequenceAction node
  let prev = null;

  let sequenceActorNodes = children
    .map((c) => {
      if (c.name !== 'node') {
        return null;
      }
      const node = buildSequenceActorNode(c.attributes, c.children, index);
      indicies[node.id] = index;
      index++;
      return node;
    })
    .filter(Boolean);

  // Build the edges (which includes constructing a sequenceAction Node to wrap the content of each edge)
  const edgesAndSequenceActionNodes: Array<[Edge, Node, Edge] | null> = children
    .map((c) => {
      if (c.name !== 'edge') {
        return null;
      }

      // Find the position of this sequenceAction
      const {to, from} = c.attributes;
      const start = indicies[from];
      const end = indicies[to];

      // Find what direction the sequenceAction node is spanning
      const direction = end - start > 0 ? 'LTR' : 'RTL';

      // Check if we are starting a new row
      // We use the row to construct the edge handles
      if (start === end) {
        row++;
      } else if (prev) {
        if (direction === 'RTL') {
          if (prev.direction === 'LTR') {
            // We are starting a new RTL row
            row++;
          } else if (start > prev.end) {
            // Even though it fits in the same row, this edge overlaps with the prior one
            row++;
          }
        } else if (prev.direction === 'RTL') {
          // We are starting a new LTR row
          row++;
        } else if (start < prev.end) {
          // Even though it fits in the same row, this edge overlaps with the prior one
          row++;
        }
      }

      const edgesAndSequenceActionNode = buildSequenceActionNodeAndEdges(
        c.attributes,
        c.children,
        direction,
        row,
        start,
        end,
      );

      prev = {start, end, direction: end - start > 0 ? 'LTR' : 'RTL'};

      return edgesAndSequenceActionNode;
    })
    .filter(Boolean);

  // Add the total number of rows as metadata to each sequenceActor
  // We use this in the UI to calculate how tall each sequenceActor node needs to be
  sequenceActorNodes = sequenceActorNodes.map((n) => {
    set(n, 'data.rows', row);
    return n;
  });

  const elements: Array<Element> = concat(
    sequenceActorNodes,
    flatten(edgesAndSequenceActionNodes),
  );

  return elements;
};
